import Style from 'ol/style/Style';
import { angle, distance, isCloseToZero } from 'cadenza/utils/math';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { assert } from 'cadenza/utils/custom-error';

/**
 * Creates an OL style for lines with image styling.
 *
 * A line segment or just segment is the connection between two consecutive points.
 * The algorithm: for each segment of the line string:
 *   1. translate canvas to the start point,
 *   2. rotate canvas, so the segment becomes a horizontal line and then
 *   3. draw the image multiple times, till the end of the segment.
 * The last image in the segment could be cut off,
 * so the rest is taken into account in the following segment display.
 *
 * @param {object} style - A style DTO.
 * @param {string} style.imageAsBase64 - The image source.
 * @return {ol.style.Style} An OL style object.
 */
export function createImageLine ({ imageAsBase64 }) {
  const img = new Image();
  img.src = imageAsBase64;

  return new Style({
    renderer: (geometry, { context, feature, pixelRatio }) => {
      if (img.width) {
        const geometryType = feature.getGeometry().getType();
        if (geometryType === GeometryType.LINE_STRING) {
          geometry = [ geometry ]; // generalize to multi line string
        }

        geometry.forEach(lineString => {
          assert(lineString.length >= 2, `Is not a valid LineString: ${lineString}`);

          let offset = 0;
          let prevPoint = lineString[0];
          for (const point of lineString) {
            if (!isCloseToZero(distance(point, prevPoint))) {
              offset = drawImagesOnLine(prevPoint, point, img, context, pixelRatio, offset);
            }
            prevPoint = point;
          }
        });
      }
    }
  });
}

function drawImagesOnLine (a, b, image, context, pixelRatio, offset) {
  context.save();
  context.translate(...a);
  const distAB = distance(a, b);
  const scaledWidth = image.width * pixelRatio;
  const scaledHeight = image.height * pixelRatio;

  // consider canvas x, y directions
  const rotation = angle([ -a[0], -a[1] ], [ -b[0], -b[1] ]);
  context.rotate(rotation);

  const y = -scaledHeight / 2;
  let x = -offset;
  // clip
  const region = new Path2D();
  region.rect(0, y, distAB, scaledHeight);
  context.clip(region);

  do {
    context.drawImage(image, x, y);
    x += scaledWidth;
  } while (x <= distAB);

  context.restore();

  return scaledWidth - (x - distAB);
}
